import { useCallback, useRef, useState } from "react";

/**
 * useVoiceState — explicit state machine for the voice pipeline.
 *
 * States:
 *   IDLE        — overlay closed or between utterances, mic off, TTS quiet
 *   LISTENING   — mic open, capturing user speech
 *   PROCESSING  — user utterance committed, awaiting agent reply
 *   SPEAKING    — TTS playing, mic muted (or VAD-only for barge-in)
 *
 * Transition guards (rejected with console.error + telemetry hook):
 *   SPEAKING  → LISTENING  only via reasons "barge_in" or "audio_ended"
 *   LISTENING → PROCESSING only via reason "final_transcript"
 *
 * All other transitions between adjacent states are permitted; transitions
 * from a state to itself are no-ops (silently ignored). Centralises what
 * `pipelineLockedRef` + `conversationStatus` previously scattered across
 * useJarvisVoice.
 */

export const STATES = Object.freeze({
  IDLE: "idle",
  LISTENING: "listening",
  PROCESSING: "processing",
  SPEAKING: "speaking",
  // CONNECTING is a UI-only sub-state during the handshake intro. We keep it
  // accepted as a target so the existing intro animation in useJarvisVoice
  // does not need a parallel field. It maps to LISTENING-eq for guard logic.
  CONNECTING: "connecting",
});

const VALID = new Set(Object.values(STATES));

const RESTRICTED_REASONS = {
  // SPEAKING → LISTENING must be either user-cut-in (barge_in) or natural end
  // of the assistant's TTS playback. Any other reason indicates a logic bug.
  [`${STATES.SPEAKING}->${STATES.LISTENING}`]: new Set(["barge_in", "audio_ended"]),
  // LISTENING → PROCESSING means we are about to dispatch the captured text
  // to the assistant; it must always be tied to a final transcript commit.
  [`${STATES.LISTENING}->${STATES.PROCESSING}`]: new Set(["final_transcript"]),
};

/**
 * Pure transition validator. Exported for tests.
 *
 * @returns {{ ok: true, to: string } | { ok: false, reason: string, message: string }}
 */
export function validateTransition(from, to, reason) {
  if (!VALID.has(from)) {
    return { ok: false, reason: "invalid_from", message: `unknown source state: ${from}` };
  }
  if (!VALID.has(to)) {
    return { ok: false, reason: "invalid_to", message: `unknown target state: ${to}` };
  }
  if (from === to) {
    // No-op: silently treated as success by transition() but flagged so
    // callers can distinguish.
    return { ok: true, to, noop: true };
  }
  const key = `${from}->${to}`;
  const restricted = RESTRICTED_REASONS[key];
  if (restricted && !restricted.has(reason)) {
    return {
      ok: false,
      reason: "restricted_transition",
      message: `${key} requires reason in {${[...restricted].join(", ")}}, got "${reason || "none"}"`,
    };
  }
  return { ok: true, to };
}

export default function useVoiceState({ onInvalid } = {}) {
  const [state, setState] = useState(STATES.IDLE);
  // Synchronous mirror — closures inside event handlers must read the *current*
  // state, not the stale one captured at render time.
  const stateRef = useRef(STATES.IDLE);

  const transition = useCallback((to, reason) => {
    const from = stateRef.current;
    const result = validateTransition(from, to, reason);
    if (!result.ok) {
      const msg = `[voice-state] invalid transition ${from} → ${to} (${reason || "no reason"}): ${result.message}`;
      console.error(msg);
      onInvalid?.({ from, to, reason, error: result.reason, message: result.message });
      return false;
    }
    if (result.noop) return true;
    stateRef.current = to;
    setState(to);
    return true;
  }, [onInvalid]);

  /**
   * Force a state without guard checks. Used by emergency paths (endConversation,
   * teardown) where we need to land on IDLE regardless of where we were. Logs
   * a warning so abuse is visible.
   */
  const force = useCallback((to, reason) => {
    if (!VALID.has(to)) {
      console.error(`[voice-state] force() to unknown state: ${to}`);
      return false;
    }
    const from = stateRef.current;
    if (from === to) return true;
    console.warn(`[voice-state] force ${from} → ${to} (${reason || "no reason"})`);
    stateRef.current = to;
    setState(to);
    return true;
  }, []);

  const is = useCallback((s) => stateRef.current === s, []);
  const get = useCallback(() => stateRef.current, []);

  return { state, transition, force, is, get, STATES };
}
